/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, effect, ElementRef, Inject, OnDestroy, OnInit, untracked, ViewChild } from '@angular/core';
import { CreditService } from '../../../../services/credit.service';
import {
  PrestamosCalculos,
  PrestamosCalculosResponse,
  CreditData,
} from '../../../../models/credit-simulator-interface';
import { catchError, concatMap, filter, map, mergeMap, takeUntil, tap } from 'rxjs/operators';
import { CommonModule, formatDate } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subject, combineLatest, lastValueFrom, of } from 'rxjs';
import { AuthService } from 'src/app/services/auth.service';
import { SecureStorageService } from 'src/app/services/secure-storage.service';
import { environment } from 'src/app/enviroments/enviroment';
import { AppModules, CreditRequestRoutes } from 'src/app/models/enums/routes.enum';
import { NgxSpinnerService } from 'ngx-spinner';
import { DataService } from 'src/app/services/data.service';
import { CreditErrorType } from 'src/app/models/enums/credit-error.enum';
import { IParser, IParserToken } from 'src/app/interfaces/parse-service.interface';
import { ICryptoToken, ICrypto } from 'src/app/interfaces/encryption-service.interface';
import { AuthenticatedUserData } from 'src/app/models/authenticated-user-data.interface';
import { LoggerService } from 'src/app/services/logger.service';
import { ResizeService } from 'src/app/services/resize.service';
import { MessageService } from 'src/app/services/message.service';
import { QueryService } from 'src/app/services/query.service';
import { InitialCreditValues } from 'src/app/models/initial-credit-values.interface';
import { GiveawayService } from 'src/app/services/giveaway.service';
import { ParametriaType } from 'src/app/models/parametria';
import { ParametriaService } from 'src/app/services/parametria.service';
import { addDays } from 'date-fns';
import { CrediflowLayoutComponent } from 'src/app/layouts/crediflow-layout/crediflow-layout.component';
import { FirstLoanMainPageComponent } from 'src/app/modules/credit-request/pages/first-loan-main-page/first-loan-main-page.component';
import { OnBoardingStartComponent } from 'src/app/modules/credit-request/pages/on-boarding-home/on-boarding-start/on-boarding-start.component';
import { OnBoardingStartLoginComponent } from 'src/app/modules/credit-request/pages/on-boarding-home/on-boarding-start-login/on-boarding-start-login.component';
import { SliderAgilCredComponent } from './slider-agil-cred/slider-agil-cred.component';
import { InfoCreditSimulatorComponent } from './info-credit-simulator/info-credit-simulator.component';
import { ButtonComponent } from 'src/app/shared/reusable/button/button.component';
import { SessionService } from 'src/app/services/session.service';

@Component({
  selector: 'app-credit-simulator',
  templateUrl: './credit-simulator.component.html',
  styleUrls: ['./credit-simulator.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    CrediflowLayoutComponent,
    FirstLoanMainPageComponent,
    OnBoardingStartComponent,
    OnBoardingStartLoginComponent,
    SliderAgilCredComponent,
    InfoCreditSimulatorComponent,
    ButtonComponent,
  ],
})
export class CreditSimulatorComponent implements OnInit, OnDestroy {
  @ViewChild('appContainer', { static: true, read: ElementRef }) appContainer!: ElementRef;
  logged = false;

  imageTemplate = '';
  data?: PrestamosCalculosResponse;
  isDisabled = false;

  sliderData!: PrestamosCalculos;
  interes!: number;
  total!: number;
  returnDate!: string;

  minAmount = 0;
  maxAmount = 0;
  stepAmount = 10;

  minDays = 0;
  maxDays = 0;
  stepDays = 0;
  dias = 0;
  amount = 0;

  public showInfo = false;
  public showLogin = false;
  public showCodeVerification = false;

  private destroyRef$: Subject<boolean> = new Subject<boolean>();

  private maxLineAmount = this.dataService.maxLineAmount;

  constructor(
    private parametriaService: ParametriaService,
    private authService: AuthService,
    private creditService: CreditService,
    private router: Router,
    private secureStorage: SecureStorageService,
    private spinner: NgxSpinnerService,
    private dataService: DataService,
    private route: ActivatedRoute,
    private logger: LoggerService,
    @Inject(ICryptoToken) private crypto: ICrypto,
    @Inject(IParserToken) private parser: IParser,
    private secureStore: SecureStorageService,
    private resize: ResizeService,
    private message: MessageService,
    private queryService: QueryService,
    private giveaway: GiveawayService,
    private sessionService: SessionService,
  ) {
    effect(() => {
      if (this.sessionService.sessionStarted()) {
        untracked(() => this.getUserLineaId());
      }
    });
  }

  ngOnInit(): void {
    this.getSession();
    this.checkIfMustShowLoginModal();
    this.dataService.setLoggedByUrl(false);
    this.checkIfUserIsLogged();
    this.resize
      .getHeightObservable(this.appContainer.nativeElement)
      .pipe(takeUntil(this.destroyRef$))
      .subscribe((height) => this.message.send({ type: 'height', message: height }));
  }

  private clearCurrentSession(): void {
    this.secureStorage.storeEncrypted(environment.loggedTokenKey, null);
    this.secureStorage.storeEncrypted(environment.loggedData, null);
    this.secureStorage.storeEncrypted(environment.loggedTokenKey, null);
    this.sessionService.clearSession();
  }

  private async getSession(): Promise<void> {
    this.clearCurrentSession();
    this.spinner.show('Inicializando plataforma...');
    await lastValueFrom(this.sessionService.startSession());

    this.spinner.show('Autenticando usuario...');
    this.getPublicToken();
    this.spinner.hide();
  }

  private async getPublicToken(): Promise<void> {
    const token = await this.authService.getToken();
    await this.secureStorage.storeEncrypted(environment.getTokenKey, token.data.token);
    if (token) this.authService.setIsLogged(true);
  }

  public nextStepLogin(): void {
    this.showInfo = false;
    this.showLogin = true;
  }

  public loginNextStep(success: boolean): void {
    this.showLogin = false;
    if (success) this.showCodeVerification = true;
  }

  public onUserLogged(): void {
    this.showLogin = false;
    this.showCodeVerification = false;
    this.logged = true;
    this.getUserLineaId();
  }

  private getUserLineaId(): void {
    this.spinner.show('Solicitando línea de crédito personalizada');
    this.creditService.getUserLineaId().subscribe(({ line_id: lineaId }) => {
      this.spinner.hide();
      this.setView(lineaId);
    });
  }

  private checkIfMustShowLoginModal(): void {
    this.route.url.subscribe((segments) => {
      if (segments.length > 0 && segments[0].path === 'login') {
        this.showInfo = true;
      }
    });
  }

  private setFlowData(lineaId: number): void {
    this.dataService.updateCreditData({ line_id: lineaId.toString() });
    this.dataService.setWhiteListFlowData({ lineaId, enabled: true });
  }

  private async checkIfUserIsLogged(): Promise<void> {
    this.route.queryParamMap.subscribe((params) => {
      const data = params.get('q');
      if (data !== null) this.authenticateUser(data);
    });
  }

  private checkIfGiveaway(): Observable<{ valid: boolean; code?: string }> {
    return this.route.queryParamMap.pipe(
      map((params) => params.get('c')),
      mergeMap((code) =>
        code
          ? this.giveaway.checkIfUrlIsUsed(code).pipe(map((res) => ({ valid: res.valid, code: res.data.link })))
          : of({ valid: false }),
      ),
    );
  }

  private async authenticateUser(cypheredData: string): Promise<void> {
    const decyphered = await this.crypto.decrypt(decodeURIComponent(cypheredData));
    const userData = this.parser.parse<AuthenticatedUserData>(decyphered);
    this.secureStorage.storeEncrypted(environment.loggedData, decyphered);
    await this.secureStore.storeEncrypted(environment.loggedTokenKey, userData.token);
    this.dataService.setLoggedByUrl(true);
    this.logged = true;
  }

  /* Check if platform is in maintenance mode*/
  private async checkIfAppIsInMaintenance(): Promise<void> {
    const maintenance = await lastValueFrom(
      this.parametriaService.isParametriaRuleEnabled(ParametriaType.IS_PLATFORM_IN_MAINTENANCE),
    );

    this.dataService.setIsPlatformInMaintenance(maintenance);
  }

  async setView(lineaId?: number): Promise<void> {
    if (lineaId) this.setFlowData(lineaId);
    this.isDisabled = true;
    this.spinner.show('Solicitando datos del crédito...');
    try {
      this.authService
        .getIsLogged()
        .pipe(filter((logged) => logged))
        .subscribe(async () => {
          await this.checkIfAppIsInMaintenance();

          combineLatest([
            this.queryService.getParamsFromRoute<InitialCreditValues>(this.route),
            this.checkIfGiveaway().pipe(
              catchError((): Observable<{ valid: boolean; code?: string }> => of({ valid: false })),
              tap((valid) => (valid.valid && valid.code ? this.dataService.setLink(valid.code) : undefined)),
              concatMap(() =>
                this.creditService
                  .getLoanCalculations(lineaId ?? environment.defaultLineaId)
                  .pipe(catchError(() => this.creditService.getLoanCalculations())),
              ),
            ),
          ])
            .pipe(takeUntil(this.destroyRef$))
            .subscribe(([initialCreditValues, prestamosCalculos]) => {
              this.getLoanCalculations(prestamosCalculos);
              if (Object.values(initialCreditValues).length > 0)
                setTimeout(
                  () => this.calculateLoan(initialCreditValues as InitialCreditValues, prestamosCalculos.data.calculos),
                  500,
                );
            });
        });
    } catch (error: any) {
      this.logger.error(error, 'Credit simulator 145');
      this.router.navigate([
        AppModules.CREDIT_REQUEST,
        CreditRequestRoutes.ERROR,
        CreditErrorType.GENERIC,
        'GET_TOKEN',
      ]);
    }
  }

  private getLoanCalculations(response: PrestamosCalculosResponse): void {
    this.sliderData = response.data;
    this.returnDate = formatDate(addDays(new Date(), 1), 'yyyy-MM-dd', 'en-US');

    [this.minAmount, this.maxAmount] = [
      this.sliderData.importes[0],
      this.sliderData.importes[this.sliderData.importes.length - 1],
    ];

    const creditData = this.dataService.getCreditData();
    if (creditData !== null && creditData.dias !== null) this.dias = creditData.dias;

    [this.minDays, this.maxDays] = [this.sliderData.dias[0], this.sliderData.dias[this.sliderData.dias.length - 1]];

    this.amount = this.minAmount;
    this.total = this.amount;

    this.updateCreditValues();
    this.isDisabled = false;
    this.spinner.hide();
    this.getDataSlider(this.minAmount, this.dias ?? this.minDays);

    const maxLineAmount = this.maxLineAmount();
    if (maxLineAmount !== undefined) this.amount = maxLineAmount;
  }

  private updateCreditValues(): void {
    const credit: CreditData = {
      importe: this.amount,
      importe_total: this.total,
      interes: this.interes,
      dias: this.dias,
      fecha_devolucion: this.returnDate,
      acepta_terminos_condiciones: false,
    };

    this.dataService.setCreditData(credit);
  }

  getDataSlider(importe?: number, dias?: number): void {
    const INTERES_POR_DEFECTO = 0;
    const TOTAL_POR_DEFECTO = 0;

    if (typeof importe === 'number') {
      this.amount = importe;
    }
    if (typeof dias === 'number') {
      this.dias = dias;
    }

    const calculos = this.sliderData?.calculos[this.amount]?.[this.dias];
    if (calculos) {
      this.updateProperties(calculos.interes, calculos.total);
    } else {
      this.updateProperties(INTERES_POR_DEFECTO, TOTAL_POR_DEFECTO);
    }

    if (this.sliderData && this.sliderData.fechas_devolucion) {
      this.returnDate = this.sliderData.fechas_devolucion[this.dias];
    }

    this.updateCreditValues();
  }

  private updateProperties(interes: number, total: number): void {
    this.interes = interes;
    this.total = total;
  }

  private calculateLoan(response: InitialCreditValues, calculos: any): void {
    const calculo = calculos[response.amount] ? calculos[response.amount][response.days] : undefined;
    if (calculo) {
      this.amount = response.amount;
      this.total = calculo.total;
      this.interes = calculo.interes;
      this.dias = response.days;
      this.returnDate = this.sliderData.fechas_devolucion[this.dias];
      this.isDisabled = false;
      this.getCredit();
    }
  }

  getCredit(): void {
    const lineaId = this.dataService.whiteListFlowData()?.lineaId;
    if (!this.isDisabled && lineaId) {
      const credit: CreditData = {
        importe: this.amount,
        importe_total: this.total,
        interes: this.interes,
        dias: this.dias,
        fecha_devolucion: this.returnDate,
        acepta_terminos_condiciones: false,
      };

      this.dataService.setCreditData(credit);

      let route = CreditRequestRoutes.CREDIT_INFO;
      let params: { [key: string]: number } = {};
      if (this.dataService.newLine() !== undefined) {
        route = CreditRequestRoutes.DUPLICATE_REQUEST;
        this.router.navigate([AppModules.CREDIT_REQUEST, CreditRequestRoutes.DUPLICATE_REQUEST]);
      } else if (this.dataService.validatedDocument() !== undefined && !this.logged) {
        route = CreditRequestRoutes.ONBOARDING_START;
        params = { step: 2 };
      } else if (this.logged) {
        route = CreditRequestRoutes.BANKING_DATA;
      }

      this.router.navigate([AppModules.CREDIT_REQUEST, route], { queryParams: params });
    }
  }

  ngOnDestroy(): void {
    this.destroyRef$.next(true);
    this.destroyRef$.unsubscribe();
  }
}
